/** Body-relative presentation paths. Coordinates are in the effect world's space (normally metres).
 * All samples write into the caller's vector; sampling never creates vectors/arrays. */
export interface BodyBounds {
  centerX: number; centerZ: number; feetY: number; height: number; radius: number;
}
const TAU = Math.PI * 2;
const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));
export function bodyMotionPhase(identity: string): { phase: number; handedness: number; delay: number } {
  let hash = 2166136261;
  for (let i = 0; i < identity.length; i++) hash = Math.imul(hash ^ identity.charCodeAt(i), 16777619);
  // Avalanche the hash: consecutive entity UUIDs must not all start within a few degrees.
  hash ^= hash >>> 16; hash = Math.imul(hash, 0x85ebca6b);
  hash ^= hash >>> 13; hash = Math.imul(hash, 0xc2b2ae35); hash ^= hash >>> 16;
  return { phase: (hash >>> 0) / 4294967296 * TAU, handedness: (hash & 1) ? -1 : 1,
    delay: ((hash >>> 8) % 71) / 1000 };
}
export function orbit(out: any, body: BodyBounds, progress: number, phase: number,
  handedness = 1, margin = 0, heightFraction = .5): any {
  const angle = phase + handedness * TAU * progress, radius = body.radius + margin;
  return out.set(body.centerX + Math.cos(angle) * radius, body.feetY + body.height * heightFraction,
    body.centerZ + Math.sin(angle) * radius);
}
/** Wrap the torso/head core, not the arm/weapon envelope. Converge only above the head. */
export function spiralUp(out: any, body: BodyBounds, progress: number, phase: number,
  handedness = 1, margin = 0, turns = 2.25): any {
  const u = clamp01(progress), y = .065 + u * 1.085;
  const above = clamp01((y - 1.10) / .05);
  const taper = 1 - above * above * (3 - 2 * above);
  const radius = (body.radius + margin + body.radius * .04 * (1 - u)) * taper;
  const angle = phase + handedness * TAU * turns * u;
  return out.set(body.centerX + Math.cos(angle) * radius, body.feetY + body.height * y,
    body.centerZ + Math.sin(angle) * radius);
}
export function convergeToPoint(out: any, from: any, point: any, progress: number): any {
  const p = clamp01(progress), u = p * p * (3 - 2 * p);
  return out.set(from.x + (point.x - from.x) * u, from.y + (point.y - from.y) * u, from.z + (point.z - from.z) * u);
}
export function burstFromPoint(out: any, point: any, direction: any, age: number,
  speed: number, gravity: number): any {
  out.copy(point).addScaledVector(direction, speed * Math.max(0, age));
  out.y -= .5 * gravity * Math.max(0, age) ** 2;
  return out;
}


const coreName = (name: string): boolean => /(?:hips|pelvis|spine\d*|chest|neck|head|headtopend)$/.test(name.toLowerCase().replace(/[^a-z0-9]/g, ''));
type CoreIntake = { indices: number[]; radii: number[]; top: number[]; bottom: number[]; box: any };
// Geometry is shared by SkeletonUtils clones. Store only numeric bind measurements, never a clone's bones.
const geometryIntake = new WeakMap<object, Map<string, CoreIntake>>();
const rootTrackers = new WeakMap<object, ReturnType<typeof makeCoreTracker>>();
let intakeScans = 0, intakeMs = 0;
export function bodyCoreCacheReceipt() { return { intakeScans, intakeMs }; }
function intake(T: any, mesh: any): CoreIntake {
  const g = mesh.geometry, bones = mesh.skeleton?.bones ?? [];
  const indices: number[] = [];
  for (let i = 0; i < bones.length; i++) if (coreName(bones[i].name) && !/headtop/i.test(bones[i].name)) indices.push(i);
  // Limit the tracker to hips, the spine/chest and head, even on a finely segmented rig.
  if (indices.length > 6) indices.splice(1, indices.length - 6);
  // A shared geometry with a genuinely different bind pose needs its own intake. Ordinary clones reuse it.
  const signature = bones.map((b: any) => b.name).join('|') + ':' + (mesh.bindMatrix?.elements.join(',') ?? '')
    + ':' + indices.map(i => mesh.skeleton.boneInverses[i].elements.join(',')).join('|');
  let cached = geometryIntake.get(g);
  if (!cached) { cached = new Map(); geometryIntake.set(g, cached); }
  const found = cached.get(signature); if (found) return found;
  const begin = performance.now();
  if (!g.boundingBox) g.computeBoundingBox();
  const data: CoreIntake = { indices, radii: indices.map(() => 0), top: indices.map(() => 0), bottom: indices.map(() => 0), box: g.boundingBox.clone() };
  const transforms = indices.map(i => new T.Matrix4().multiplyMatrices(mesh.skeleton.boneInverses[i], mesh.bindMatrix).elements);
  const slots = new Int16Array(bones.length); slots.fill(-1);
  for (let i = 0; i < indices.length; i++) slots[indices[i]!] = i;
  const pos = g.attributes.position, weights = g.attributes.skinWeight, skin = g.attributes.skinIndex;
  // One bind-position scan per geometry/rig layout. No posed vertices or cast-time surface search.
  intakeScans++;
  if (indices.length && weights && skin) for (let v = 0; v < pos.count; v++) {
    let total = 0, best = -1, largest = 0;
    for (let k = 0; k < 4; k++) {
      const slot = slots[skin.getComponent(v, k)] ?? -1, weight = weights.getComponent(v, k);
      if (slot >= 0) { total += weight; if (weight > largest) { largest = weight; best = slot; } }
    }
    if (total < .7 || best < 0) continue; // Limbs/shoulder arms never enlarge the core.
    const e = transforms[best]!, x = pos.getX(v), y = pos.getY(v), z = pos.getZ(v);
    const lx = e[0] * x + e[4] * y + e[8] * z + e[12];
    const ly = e[1] * x + e[5] * y + e[9] * z + e[13];
    const lz = e[2] * x + e[6] * y + e[10] * z + e[14];
    data.radii[best] = Math.max(data.radii[best]!, Math.sqrt(lx * lx + lz * lz));
    data.top[best] = Math.max(data.top[best]!, ly); data.bottom[best] = Math.min(data.bottom[best]!, ly);
  }
  intakeMs += performance.now() - begin;
  cached.set(signature, data); return data;
}
function makeCoreTracker(T: any, world: any, root: any) {
  const nodes: any[] = [], cores: Array<{ bone: any; radius: number; top: number; bottom: number }> = [];
  // Every unskinned-core mesh, not just the first: a multi-part prop (training dummy = base + pole + body) is the union.
  let sprite: any = null; const statics: Array<{ node: any; box: any }> = [];
  const visit = (n: any): void => {
    if (!n || n.visible === false || n.userData?.ftMagicVfx || n.userData?.ftStatusEffects || n.userData?.ftQueryOnly || n.userData?.ftAvatarEquipment || n.isLine || n.isPoints) return;
    if (n.isSprite) { sprite ??= n; nodes.push(n); }
    else if (n.isMesh && n.geometry?.attributes.position) {
      nodes.push(n); const d = intake(T, n);
      if (d.indices.length && !cores.length) for (let i = 0; i < d.indices.length; i++) cores.push({ bone: n.skeleton.bones[d.indices[i]!], radius: d.radii[i]!, top: d.top[i]!, bottom: d.bottom[i]! });
      statics.push({ node: n, box: d.box });
    }
    for (const child of n.children ?? []) visit(child);
  };
  visit(root);
  const coreChain: any[] = [], chainSeen = new Set<any>();
  for (const core of cores) {
    const path: any[] = []; for (let n = core.bone; n && n !== root && n !== world; n = n.parent) path.push(n);
    for (let i = path.length - 1; i >= 0; i--) if (!chainSeen.has(path[i])) { chainSeen.add(path[i]); coreChain.push(path[i]); }
  }
  const inverse = new T.Matrix4(), frame = new T.Matrix4(), point = new T.Vector3(), size = new T.Vector3();
  // Static bodies: the parts' union in ROOT space, measured once; each tick only re-poses that one box.
  const staticBox = new T.Box3(), partBox = new T.Box3();
  if (!cores.length && statics.length && root) {
    root.updateWorldMatrix(true, true); inverse.copy(root.matrixWorld).invert();
    for (const part of statics) staticBox.union(partBox.copy(part.box).applyMatrix4(frame.multiplyMatrices(inverse, part.node.matrixWorld)));
  }
  const positions = new Float32Array(cores.length * 3), scales = new Float32Array(cores.length);
  const bounds: BodyBounds = { centerX: 0, centerZ: 0, feetY: 0, height: 1.8, radius: .25 };
  const emit = new Float32Array(12 * 3), slots = new Uint8Array(12);
  for (let i = 0; i < 12; i++) {
    const slot = cores.length ? Math.min(cores.length - 1, Math.floor(i / 12 * cores.length)) : 0;
    slots[i] = slot; const a = i * 2.399963229728653, r = cores[slot]?.radius || .2;
    emit[i * 3] = Math.cos(a) * r * 1.06; emit[i * 3 + 1] = (cores[slot]?.top ?? .08) * .25; emit[i * 3 + 2] = Math.sin(a) * r * 1.06;
  }
  const sample = (position: any, fallbackHeight: number): BodyBounds => {
    // Refresh the root and a handful of core-bone ancestor chains. Never refresh the full actor subtree.
    world.updateWorldMatrix(true, false); inverse.copy(world.matrixWorld).invert(); root?.updateWorldMatrix(true, false);
    bounds.centerX = position.x; bounds.centerZ = position.z; bounds.feetY = position.y;
    bounds.height = Math.max(.15, fallbackHeight); bounds.radius = bounds.height * .13;
    if (cores.length) {
      for (const node of coreChain) node.updateWorldMatrix(false, false);
      let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity, topY = position.y;
      for (let i = 0; i < cores.length; i++) {
        const c = cores[i]!; frame.multiplyMatrices(inverse, c.bone.matrixWorld);
        point.setFromMatrixPosition(frame); size.setFromMatrixScale(frame);
        point.toArray(positions, i * 3); scales[i] = Math.max(Math.abs(size.x), Math.abs(size.y), Math.abs(size.z));
        minX = Math.min(minX, point.x); maxX = Math.max(maxX, point.x); minZ = Math.min(minZ, point.z); maxZ = Math.max(maxZ, point.z);
        topY = Math.max(topY, point.y + c.top * scales[i]!);
      }
      bounds.centerX = (minX + maxX) * .5; bounds.centerZ = (minZ + maxZ) * .5;
      bounds.height = Math.max(.15, topY - bounds.feetY);
      let radius = bounds.height * .08;
      for (let i = 0; i < cores.length; i++) radius = Math.max(radius, Math.hypot(positions[i * 3]! - bounds.centerX, positions[i * 3 + 2]! - bounds.centerZ) + cores[i]!.radius * scales[i]!);
      bounds.radius = radius;
    } else if (sprite) {
      sprite.updateWorldMatrix(true, false); frame.multiplyMatrices(inverse, sprite.matrixWorld);
      point.setFromMatrixPosition(frame); size.setFromMatrixScale(frame);
      bounds.centerX = point.x; bounds.centerZ = point.z; bounds.feetY = point.y - Math.abs(size.y) * sprite.center.y;
      bounds.height = Math.max(.15, Math.abs(size.y)); bounds.radius = Math.abs(size.x) * .5;
    } else if (!staticBox.isEmpty()) {
      // Pose the root-space centre and scale the root-space extents, so a turned body is not inflated to a world AABB.
      frame.multiplyMatrices(inverse, root.matrixWorld); size.setFromMatrixScale(frame);
      staticBox.getCenter(point).applyMatrix4(frame);
      const sx = (staticBox.max.x - staticBox.min.x) * Math.abs(size.x), sy = (staticBox.max.y - staticBox.min.y) * Math.abs(size.y);
      const sz = (staticBox.max.z - staticBox.min.z) * Math.abs(size.z);
      bounds.centerX = point.x; bounds.centerZ = point.z;
      bounds.height = Math.max(.15, sy); bounds.feetY = point.y - sy * .5;
      bounds.radius = Math.hypot(sx, sz) * .5;
    }
    return bounds;
  };
  const surfacePoint = (out: any, index: number): any => {
    const i = index % 12, at = i * 3;
    if (cores.length) {
      const c = cores[slots[i]!]!; frame.multiplyMatrices(inverse, c.bone.matrixWorld);
      return out.fromArray(emit, at).applyMatrix4(frame); // Cached bone-local offsets, never a pinned skinned vertex.
    }
    const a = i * 2.399963229728653;
    return out.set(bounds.centerX + Math.cos(a) * bounds.radius * .85, bounds.feetY + bounds.height * (.40 + i % 6 * .10), bounds.centerZ + Math.sin(a) * bounds.radius * .85);
  };
  // Still the same representation: every discovered node is visible and still parented under this root.
  const attached = (n: any): boolean => { for (let p = n; p; p = p.parent) { if (p.visible === false) return false; if (p === root) return true; } return false; };
  const valid = (): boolean => nodes.length > 0 && nodes.every(attached);
  return { root, world, bounds, sample, surfacePoint, valid, pinSurface: () => {}, nodeCount: nodes.length, coreBoneCount: cores.length, skinInfluenceBoxes: 0 };
}
/** Reused by preload and casts. Weak keys release the intake together with the actor/model. */
export function createBodyBoundsTracker(T: any, world: any, root: any) {
  if (root) {
    // Re-discover when meshes/skeletons under the root were swapped, removed or hidden; never cache an empty discovery.
    const cached = rootTrackers.get(root); if (cached && cached.world === world && cached.valid()) return cached;
    const tracker = makeCoreTracker(T, world, root);
    if (tracker.nodeCount) rootTrackers.set(root, tracker); else rootTrackers.delete(root);
    return tracker;
  }
  return makeCoreTracker(T, world, root);
}
